/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, signal, computed } from '@angular/core';
import { ReviewFieldKey } from '../../interfaces/review-fields.types';
import { anjanaFieldToReviewFieldMap, reviewFieldToAnjanaFieldMap } from './review-fields.mapper';

export interface FieldPreviousReview {
  reviewers: string[];
}
const REVIEWER_LABELS: Record<string, string> = {
  RT: 'Revisor Técnico',
  RJ: 'Revisor Jurídico',
  RDPD: 'Revisor de Protección de Datos',
  RF: 'Revisor Funcional',
  GA: 'Grupo de Apoyo',
  JS: 'Jefe de Servicio',
};
const backendSelectedRoleMap: Record<string, string> = {
  selectedSupportGroup: 'GA',
  selectedRevisorTecnico: 'RT',
  selectedRevisorJuridico: 'RJ',
  selectedRevisorFuncional: 'RF',
  selectedRevisorDpd: 'RDPD',
};

@Injectable({ providedIn: 'root' })
export class ReviewFieldsService {
  private readonly reviewState = signal<Partial<Record<ReviewFieldKey, boolean>>>({});
  readonly markedFields$ = computed<ReviewFieldKey[]>(() =>
    Object.entries(this.reviewState())
      .filter(([, marked]) => marked)
      .map(([key]) => key as ReviewFieldKey)
  );
  private readonly previousReviewState = signal<Partial<Record<ReviewFieldKey, FieldPreviousReview>>>({});

  isFieldMarked(fieldKey: ReviewFieldKey): boolean {
    return this.reviewState()[fieldKey] ?? false;
  }

  markField(fieldKey: ReviewFieldKey, marked: boolean): void {
    this.reviewState.update((state) => ({
      ...state,
      [fieldKey]: marked,
    }));
  }

  toggleField(fieldKey: ReviewFieldKey): void {
    this.markField(fieldKey, !this.isFieldMarked(fieldKey));
  }

  getMarkedFieldKeys(): ReviewFieldKey[] {
    return this.markedFields$();
  }
  mapMarkedFieldsToBackend(): string[] {
    return this.getMarkedFieldKeys()
      .map((fieldKey) => reviewFieldToAnjanaFieldMap[fieldKey])
      .filter(Boolean);
  }

  restoreMarkedFields(markedFields: ReviewFieldKey[]): void {
    const nextState: Partial<Record<ReviewFieldKey, boolean>> = {};

    markedFields.forEach((fieldKey) => {
      nextState[fieldKey] = true;
    });

    this.reviewState.set(nextState);
  }
  reset(): void {
    this.reviewState.set({});
  }
  hasPreviousReview(fieldKey: ReviewFieldKey): boolean {
    return !!this.previousReviewState()[fieldKey]?.reviewers?.length;
  }

  getPreviousReviewers(fieldKey: ReviewFieldKey): string[] {
    return this.previousReviewState()[fieldKey]?.reviewers ?? [];
  }
  loadPreviousReviewsFromBackend(data: any): void {
    const nextPreviousState: Partial<Record<ReviewFieldKey, FieldPreviousReview>> = {};
    const nextMarkedState: Partial<Record<ReviewFieldKey, boolean>> = {};

    Object.entries(backendSelectedRoleMap).forEach(([backendKey, roleCode]) => {
      const selectedAnjanaFields: string[] | null = data?.[backendKey];

      if (!Array.isArray(selectedAnjanaFields)) {
        return;
      }

      selectedAnjanaFields.forEach((anjanaField) => {
        const fieldKey = anjanaFieldToReviewFieldMap[anjanaField];

        if (!fieldKey) {
          return;
        }
        nextMarkedState[fieldKey] = true;
        const reviewers = nextPreviousState[fieldKey]?.reviewers ?? [];

        if (!reviewers.includes(roleCode)) {
          nextPreviousState[fieldKey] = {
            reviewers: [...reviewers, roleCode],
          };
        }
      });
    });

    this.previousReviewState.set(nextPreviousState);
    this.reviewState.set(nextMarkedState);
  }

  getReviewerLabels(codes: string[]): string[] {
    return codes.map((code) => REVIEWER_LABELS[code] ?? code);
  }
  getPreviousReviewTooltipText(fieldKey: ReviewFieldKey): string {
    const reviewers = this.getPreviousReviewers(fieldKey);

    if (!reviewers.length) {
      return '';
    }
    const labels = this.getReviewerLabels(reviewers);

    return `Campo marcado por: ${labels.join(', ')}`;
  }
}
